/* eslint-disable import/no-dynamic-require */

/**
 * @module lib/AudioLoader
 * Provides functions for dynamically loading the appropriate audio service providers
 * given the runtime context. Uses AUDIO_HARNESSES global created in webpack config for
 * the locations of the harnesses.
 */
import { Map } from 'immutable';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import unionBy from 'lodash/unionBy';
import UaParser from 'ua-parser-js';

import * as AudioConstants from '../../constants';
import * as D from '../../deps';

import * as AudioPlayback from './AudioPlayback';

export const uaParser = new UaParser();
const player = AudioPlayback;
let isInitialized = false;

const logger = D.Logger.child({
  component: 'lib/AudioPlayback/AudioLoader',
  level: D.Utils.DebugSettings.getAudioLogLevel(),
});

export function getIsInitialized() {
  return isInitialized;
}

/**
 * Parses the tunerVar that indicates which browsers we believe support HTML5 audio
 * sufficiently and returns whether we should force flash
 *
 * @param  {object} browserConfig - a tunerVar indicating HTML5 audio support
 * @param  {object} parsedUa      - the parsed user agent
 * @param {object} [playerOptions]  - optional player options
 * @returns {boolean}              - a flag whether to force flash
 */
export function getForceFlashFlag(browserConfig, parsedUa, playerOptions = null) {
  if (playerOptions && !isUndefined(playerOptions.tryForceFlash)) {
    return playerOptions.tryForceFlash;
  }

  const defaultIfNoOS = false;
  const defaultValue = true;

  if (!(parsedUa.os && parsedUa.browser) || !Map.isMap(browserConfig)) {
    logger.debug('Missing audio player supported browsers. Forcing flash.');
    return defaultValue;
  }

  let configValue = browserConfig.get(parsedUa.os.name);

  if (!configValue) {
    logger.debug('No configuration for OS, NOT forcing flash.');
    return defaultIfNoOS;
  }

  // compareVersionNumbers can return NaN, but the comparisons will return false if that's the case
  if (
    Map.isMap(configValue) &&
    D.Utils.NumberUtils.compareVersionNumbers(parsedUa.os.version, configValue.get('minVersion')) >=
      0
  ) {
    configValue = configValue.get('browsers');
  } else {
    logger.debug('Not a supported browser for audio player. Forcing flash.');
    return defaultValue;
  }

  configValue = configValue && configValue.get(parsedUa.browser.name);

  if (
    configValue &&
    D.Utils.NumberUtils.compareVersionNumbers(
      parsedUa.browser.version,
      configValue.get('minVersion'),
    ) >= 0
  ) {
    // supported is true if we want to support HTML5, so if false we force flash
    const forceFlash = !configValue.get('supported');
    logger.debug('Forcing flash for current browser? ', forceFlash);
    return forceFlash;
  }

  logger.debug('Defaulting to forcing flash.');
  return defaultValue;
}

/*
 * Returns the initialized player or throws an error.
 * @return {Object|Error} returns the initialized audio library or error
 */
export function getPlayer() {
  if (isInitialized) {
    return player;
  }
  throw new Error('Player not initialized');
}

/*
 * Get the harness configs used to playback audio for each audio encoding type (e.g. 'mp3', 'm4a', etc...)
 * for the current browser
 *
 * @returns {[HarnessConfig]} An array of harness configs
 */
export function getHarnessConfig() {
  const result = uaParser.getResult();
  const environmentName = result.browser.name;
  const harnessConfigs = unionBy(
    AudioConstants.HarnessConfig[environmentName],
    AudioConstants.HarnessConfig.Default,
    'name',
  );
  // SXM is preferred when possible, so it needs to come first
  harnessConfigs.unshift(AudioConstants.SXMHarnessConfig);
  return harnessConfigs;
}

// Gets the maximum amount of providers we want to keep around in the audio providers
// there is a tradeoff here. More elements means we can switch between common stations quicker
// but in some browsers (Safari) you can't create elements in the background so it's sometimes
// required to only use one provider at a time.
export function getMaxProviders() {
  const result = uaParser.getResult();
  const environmentName = result.browser.name;
  return (
    AudioConstants.maxProviderConfig[environmentName] || AudioConstants.maxProviderConfig.Default
  );
}

/**
 * Initializes the audio playback library
 *
 * @param {Array} audioHarnesses - The audio provider harness (e.g. mediaElementHarness)
 * @param {Array} harnessConfigs - Mappings from getHarnessConfig
 * @param {object} supportedBrowsers - A tunerVar that indicates which browsers we believe support
 * HTML5 audio sufficiently
 * @param {object} playerOptions - Some options that the caller of the function can use to configure
 * the initialization of the audio library.
 * @param {boolean} playerOptions.tryForceFlash - This value will override any value passed from the runtime config and
 * will request the audio to try to force flash if it can.
 * @param {boolean} isForced - true if we're manually triggering re-initializing
 * @returns {AudioPlayback} - The initialized audio playback library
 */
export function initializeAudioPlayback(
  audioHarnesses,
  harnessConfigs,
  supportedBrowsers,
  playerOptions = {},
  isForced,
) {
  if (!isArray(audioHarnesses) || isEmpty(audioHarnesses)) {
    throw new Error('Audio player could not initialize without all harnesses');
  }

  if (!isArray(harnessConfigs) || isEmpty(harnessConfigs)) {
    throw new Error('Audio player could not initialize without harnessConfigs');
  }

  const tryForceFlash = getForceFlashFlag(supportedBrowsers, uaParser.getResult(), playerOptions);
  const maxProviders = getMaxProviders();
  AudioPlayback.initialize(
    {
      maxProviders,
      logger,
      tryForceFlash,
      harnessConfigs,
      audioData: playerOptions.audioData,
    },
    audioHarnesses,
    isForced,
  );

  isInitialized = true;
  return AudioPlayback;
}

/**
 *
 * @param {HarnessConfig} harnessConfig
 * @param {Function} resolve
 * @param {Function} reject
 * @returns {Promise.<Harness>}
 * @private
 */
function _requireHarness(harnessConfig, resolve, reject) {
  const harnessName = harnessConfig.name;

  if (harnessName === AudioConstants.AudioHarnessName.MEDIA_ELEMENT) {
    return System.import(
      /* webpackChunkName: "media-element-harness" */ AUDIO_HARNESSES.MEDIAELEMENT,
    ).then(mediaElementHarness => resolve(mediaElementHarness));
  } else if (harnessName === AudioConstants.AudioHarnessName.STROBE) {
    return System.import(/* webpackChunkName: "strobe-harness" */ AUDIO_HARNESSES.STROBE).then(
      strobePlayerHarness => resolve(strobePlayerHarness),
    );
  } else if (harnessName === AudioConstants.AudioHarnessName.SXM_AUDIO_PLAYER) {
    return System.import(
      /* webpackChunkName: "sxm-harness" */ AUDIO_HARNESSES.SXM_AUDIO_PLAYER,
    ).then(sxmHarness => resolve(sxmHarness));
  }

  reject(new D.Errors.ApplicationError('unknown harness: ' + harnessName));
}

/**
 * A function that initializes the audio playback library given an array of harness configs.
 *
 * @param {[HarnessConfig]} harnessConfigs - Configs for the harnesses to load
 * @returns {[Promise.<Harness>]} - An array of promises that each resolve to an audio harnesses
 */
export default function loadHarnesses(harnessConfigs) {
  return map(harnessConfigs, harnessConfig => {
    return new Promise((res, rej) => {
      _requireHarness(harnessConfig, res, rej);
    });
  });
}
